import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut, type User } from 'firebase/auth';
import { get, onValue, ref } from 'firebase/database';
import { Loader2, RefreshCw } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { auth, database } from '../services/firebase';

type UserProfile = {
  uid?: string;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  createdAt?: number;
  needsOrganizationSetup?: boolean;
  organizationId?: string;
  organizationMembershipStatus?: string;
  superAdmin?: boolean;
};

type PetRecord = {
  ownerId?: string | null;
};

type PetSubCounts = {
  petCount: number;
  petPictures: number;
  petNotes: number;
  petExternalNotes: number;
  petMedications: number;
  petFoods: number;
  petAllergies: number;
  petVaccinations: number;
  petConditions: number;
};

type UserReportRow = PetSubCounts & {
  id: string;
  displayName: string;
  email?: string | null;
  createdAt?: number;
  superAdmin?: boolean;
};

type ReportTotals = PetSubCounts & {
  userCount: number;
  familyCount: number;
  familyMemberLinks: number;
  familyMemberUsers: number;
};

const ZERO_COUNTS: PetSubCounts = {
  petCount: 0,
  petPictures: 0,
  petNotes: 0,
  petExternalNotes: 0,
  petMedications: 0,
  petFoods: 0,
  petAllergies: 0,
  petVaccinations: 0,
  petConditions: 0,
};

const INITIAL_TOTALS: ReportTotals = {
  userCount: 0,
  familyCount: 0,
  familyMemberLinks: 0,
  familyMemberUsers: 0,
  ...ZERO_COUNTS,
};

function countEntries(value: unknown): number {
  if (!value) {
    return 0;
  }
  if (Array.isArray(value)) {
    return value.filter(Boolean).length;
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length;
  }
  return 0;
}

function getDisplayName(profile: UserProfile | null | undefined, fallbackId: string): string {
  const name = [profile?.firstName, profile?.lastName]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(' ');
  return (
    profile?.displayName?.trim() ||
    name ||
    profile?.email?.trim() ||
    fallbackId
  );
}

export default function Reports() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [rows, setRows] = useState<UserReportRow[]>([]);
  const [totals, setTotals] = useState<ReportTotals>(INITIAL_TOTALS);
  const [lastUpdated, setLastUpdated] = useState<number | null>(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    if (typeof window === 'undefined') {
      return false;
    }
    return localStorage.getItem('theme') === 'dark';
  });

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, user => {
      setCurrentUser(user);
      setAuthChecked(true);
      if (!user) {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/reports' },
        });
      }
    });
    return unsubscribe;
  }, [navigate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const storedTheme = localStorage.getItem('theme');
    if (storedTheme === 'dark') {
      setIsDarkMode(true);
    }
    if (storedTheme === 'light') {
      setIsDarkMode(false);
    }
  }, []);

  useEffect(() => {
    if (!currentUser) {
      setProfile(null);
      setProfileLoading(false);
      return;
    }

    const profileRef = ref(database, `users/${currentUser.uid}`);
    setProfileLoading(true);
    const unsubscribe = onValue(
      profileRef,
      snapshot => {
        const value = snapshot.val() as UserProfile | null;
        if (value?.needsOrganizationSetup && !value?.superAdmin) {
          setProfileLoading(false);
          navigate('/organization/create', {
            replace: true,
            state: { returnTo: '/reports' },
          });
          return;
        }
        setProfile(value);
        setProfileLoading(false);
      },
      error => {
        console.warn('Failed to load profile for reports view', error);
        setProfile(null);
        setProfileLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser, navigate]);

  const isSuperAdmin = profile?.superAdmin === true;

  const loadReports = useCallback(async () => {
    if (!isSuperAdmin) {
      setRows([]);
      setTotals(INITIAL_TOTALS);
      setLastUpdated(null);
      setReportLoading(false);
      setReportError(null);
      return;
    }

    setReportLoading(true);
    setReportError(null);

    try {
      const [
        usersSnap,
        petsSnap,
        familiesSnap,
        familyMembersSnap,
        petPicturesSnap,
        petNotesSnap,
        petExternalNotesSnap,
        petMedicationsSnap,
        petFoodsSnap,
        petAllergiesSnap,
        petVaccinationsSnap,
        petConditionsSnap,
      ] = await Promise.all([
        get(ref(database, 'users')),
        get(ref(database, 'pets')),
        get(ref(database, 'families')),
        get(ref(database, 'familyMembers')),
        get(ref(database, 'petPictures')),
        get(ref(database, 'petNotes')),
        get(ref(database, 'petExternalNotes')),
        get(ref(database, 'petMedications')),
        get(ref(database, 'petFoods')),
        get(ref(database, 'petAllergies')),
        get(ref(database, 'petVaccinations')),
        get(ref(database, 'petConditions')),
      ]);

      const usersValue = usersSnap.val() as Record<string, UserProfile> | null;
      const petsValue = petsSnap.val() as Record<string, PetRecord> | null;
      const familiesValue = familiesSnap.val() as Record<string, unknown> | null;
      const familyMembersValue = familyMembersSnap.val() as Record<string, unknown> | null;

      const petOwnerById: Record<string, string> = {};
      const countsByUser: Record<string, PetSubCounts> = {};

      const ensureCounts = (userId: string) => {
        if (!countsByUser[userId]) {
          countsByUser[userId] = { ...ZERO_COUNTS };
        }
      };

      let totalPetCount = 0;
      if (petsValue) {
        Object.entries(petsValue).forEach(([petId, record]) => {
          totalPetCount += 1;
          const ownerId = typeof record?.ownerId === 'string' ? record.ownerId : '';
          if (ownerId) {
            petOwnerById[petId] = ownerId;
            ensureCounts(ownerId);
            countsByUser[ownerId].petCount += 1;
          }
        });
      }

      const totalsNext: ReportTotals = {
        ...INITIAL_TOTALS,
        userCount: usersValue ? Object.keys(usersValue).length : 0,
        familyCount: familiesValue ? Object.keys(familiesValue).length : 0,
      };
      totalsNext.petCount = totalPetCount;

      const familyMemberUsers = new Set<string>();
      let familyMemberLinks = 0;
      if (familyMembersValue && typeof familyMembersValue === 'object') {
        Object.entries(familyMembersValue).forEach(([userId, members]) => {
          if (userId) {
            familyMemberUsers.add(userId);
          }
          familyMemberLinks += countEntries(members);
          if (members && typeof members === 'object') {
            Object.keys(members as Record<string, unknown>).forEach(memberId => {
              if (memberId) {
                familyMemberUsers.add(memberId);
              }
            });
          }
        });
      }
      totalsNext.familyMemberLinks = familyMemberLinks;
      totalsNext.familyMemberUsers = familyMemberUsers.size;

      const applySubCounts = (
        data: Record<string, unknown> | null | undefined,
        key: keyof Omit<PetSubCounts, 'petCount'>,
      ) => {
        if (!data || typeof data !== 'object') {
          return;
        }
        Object.entries(data).forEach(([petId, records]) => {
          const count = countEntries(records);
          totalsNext[key] += count;
          const ownerId = petOwnerById[petId];
          if (ownerId) {
            ensureCounts(ownerId);
            countsByUser[ownerId][key] += count;
          }
        });
      };

      applySubCounts(petPicturesSnap.val() as Record<string, unknown> | null, 'petPictures');
      applySubCounts(petNotesSnap.val() as Record<string, unknown> | null, 'petNotes');
      applySubCounts(petExternalNotesSnap.val() as Record<string, unknown> | null, 'petExternalNotes');
      applySubCounts(petMedicationsSnap.val() as Record<string, unknown> | null, 'petMedications');
      applySubCounts(petFoodsSnap.val() as Record<string, unknown> | null, 'petFoods');
      applySubCounts(petAllergiesSnap.val() as Record<string, unknown> | null, 'petAllergies');
      applySubCounts(petVaccinationsSnap.val() as Record<string, unknown> | null, 'petVaccinations');
      applySubCounts(petConditionsSnap.val() as Record<string, unknown> | null, 'petConditions');

      const rowsNext: UserReportRow[] = [];
      if (usersValue) {
        Object.entries(usersValue).forEach(([userId, userProfile]) => {
          const counts = countsByUser[userId] ?? { ...ZERO_COUNTS };
          rowsNext.push({
            id: userId,
            displayName: getDisplayName(userProfile, userId),
            email: userProfile.email ?? null,
            createdAt: userProfile.createdAt,
            superAdmin: userProfile.superAdmin,
            ...counts,
          });
        });
      }

      const missingUsers = Object.keys(countsByUser).filter(userId => !usersValue?.[userId]);
      missingUsers.forEach(userId => {
        const counts = countsByUser[userId] ?? { ...ZERO_COUNTS };
        rowsNext.push({
          id: userId,
          displayName: `Unknown user (${userId})`,
          email: null,
          ...counts,
        });
      });

      rowsNext.sort((a, b) => {
        const aCreated = typeof a.createdAt === 'number' ? a.createdAt : 0;
        const bCreated = typeof b.createdAt === 'number' ? b.createdAt : 0;
        if (bCreated !== aCreated) {
          return bCreated - aCreated;
        }
        return a.displayName.localeCompare(b.displayName);
      });

      setRows(rowsNext);
      setTotals(totalsNext);
      setLastUpdated(Date.now());
    } catch (error) {
      console.warn('Failed to load reports', error);
      setReportError('Failed to load reports. Please try again.');
      setRows([]);
      setTotals(INITIAL_TOTALS);
      setLastUpdated(null);
    } finally {
      setReportLoading(false);
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (!isSuperAdmin) {
      setRows([]);
      setTotals(INITIAL_TOTALS);
      setLastUpdated(null);
      setReportLoading(false);
      setReportError(null);
      return;
    }

    void loadReports();
  }, [isSuperAdmin, loadReports]);

  const handleThemeToggle = () => {
    setIsDarkMode(prev => {
      const next = !prev;
      if (typeof window !== 'undefined') {
        localStorage.setItem('theme', next ? 'dark' : 'light');
      }
      return next;
    });
  };

  const handleSignIn = () => {
    navigate('/signin', { state: { returnTo: '/reports' } });
  };

  const handleCreateAccount = () => {
    navigate('/register', { state: { returnTo: '/reports' } });
  };

  const handleLogout = () => {
    void signOut(auth)
      .then(() => {
        navigate('/signin', {
          replace: true,
          state: { returnTo: '/reports' },
        });
      })
      .catch(error => {
        console.warn('Failed to sign out', error);
      });
  };

  const formatCount = useMemo(() => new Intl.NumberFormat().format, []);
  const mutedTextClass = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const cardClass = `${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-white text-slate-900 ring-slate-200'} rounded-2xl p-4 ring-1`;
  const tableHeaderClass = `${isDarkMode ? 'text-slate-300' : 'text-slate-500'} text-xs font-semibold uppercase tracking-wide`;
  const tableCellClass = `${isDarkMode ? 'text-slate-100' : 'text-slate-900'} px-4 py-3 text-sm`;

  if (!authChecked) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-900 text-slate-100">
        <div className="flex items-center gap-3 text-lg font-semibold">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
          Checking your access...
        </div>
      </div>
    );
  }

  if (!currentUser) {
    return null;
  }

  const displayName =
    profile?.displayName?.trim() ||
    currentUser.displayName?.trim() ||
    currentUser.email ||
    'there';

  const hasOrganizationAccess = Boolean(profile?.organizationId || profile?.superAdmin);
  return (
    <DashboardLayout
      isDarkMode={isDarkMode}
      onToggleTheme={handleThemeToggle}
      currentUser={currentUser}
      onSignIn={handleSignIn}
      onCreateAccount={handleCreateAccount}
      onLogout={handleLogout}
      needsOrganizationSetup={profile?.needsOrganizationSetup}
      hasOrganizationAccess={hasOrganizationAccess}
      organizationMembershipStatus={profile?.organizationMembershipStatus}
      hideNavigation
    >
      <div className="space-y-6">
        <header className="rounded-3xl bg-blue-500/10 px-6 py-5">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-blue-500">Reports</h1>
              <p className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'} mt-2 text-sm`}>
                Monitor usage across users, pets, families, and pet content.
              </p>
            </div>
            {isSuperAdmin ? (
              <button
                type="button"
                onClick={() => void loadReports()}
                className={`${isDarkMode ? 'bg-slate-900/70 text-slate-100 hover:bg-slate-900' : 'bg-white text-slate-700 hover:bg-slate-50'} inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-slate-200/40 transition`}
                disabled={reportLoading}
              >
                {reportLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : (
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                )}
                Refresh
              </button>
            ) : null}
          </div>
        </header>

        {profileLoading ? (
          <div className="flex items-center gap-3 rounded-3xl bg-yellow-500/10 px-5 py-3 text-yellow-600">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Loading your profile...
          </div>
        ) : null}

        {!isSuperAdmin && !profileLoading ? (
          <div className={`${isDarkMode ? 'bg-rose-500/10 text-rose-100 ring-rose-500/40' : 'bg-rose-100 text-rose-700 ring-rose-300'} rounded-3xl px-6 py-5 ring-1`}>
            <h2 className="text-lg font-semibold">Access restricted</h2>
            <p className="mt-2 text-sm">
              Only super administrators can view platform reports. If you need access, contact your platform administrator.
            </p>
          </div>
        ) : null}

        {isSuperAdmin ? (
          <>
            {reportError ? (
              <div className={`${isDarkMode ? 'bg-rose-500/10 text-rose-100 ring-rose-500/40' : 'bg-rose-100 text-rose-700 ring-rose-300'} rounded-3xl px-6 py-5 ring-1`}>
                <p className="text-sm font-medium">{reportError}</p>
              </div>
            ) : null}

            <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <div className={cardClass}>
                <p className={mutedTextClass}>Users</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.userCount)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Pets</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petCount)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Families</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.familyCount)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Family Members</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.familyMemberUsers)}</p>
                <p className="mt-1 text-xs text-slate-400">{formatCount(totals.familyMemberLinks)} total links</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Pet Pictures</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petPictures)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Pet Notes</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petNotes)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>External Notes</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petExternalNotes)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Medications</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petMedications)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Foods</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petFoods)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Allergies</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petAllergies)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Vaccinations</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petVaccinations)}</p>
              </div>
              <div className={cardClass}>
                <p className={mutedTextClass}>Conditions</p>
                <p className="mt-2 text-2xl font-semibold">{formatCount(totals.petConditions)}</p>
              </div>
            </section>

            <section className={`${isDarkMode ? 'bg-slate-900/60 ring-slate-800' : 'bg-white ring-slate-200'} rounded-3xl p-6 ring-1`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold">User activity</h2>
                  <p className={`${mutedTextClass} mt-1 text-sm`}>
                    Pet counts and related content per user.
                  </p>
                </div>
                {lastUpdated ? (
                  <p className={`${mutedTextClass} text-xs`}>
                    Updated {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(lastUpdated))}
                  </p>
                ) : null}
              </div>

              {reportLoading ? (
                <div className="mt-4 flex items-center gap-3 rounded-2xl bg-blue-500/10 px-4 py-3 text-blue-500">
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  Loading reports...
                </div>
              ) : null}

              {!reportLoading && rows.length === 0 ? (
                <div className={`${isDarkMode ? 'bg-slate-900/60 text-slate-100 ring-slate-800' : 'bg-slate-100 text-slate-600 ring-slate-200'} mt-4 rounded-2xl px-5 py-4 text-sm ring-1`}>
                  No user data found.
                </div>
              ) : null}

              {rows.length ? (
                <div className="mt-4 w-full max-w-full overflow-x-auto">
                  <table className="min-w-[1100px] w-full border-collapse">
                    <thead>
                      <tr className={tableHeaderClass}>
                        <th className="px-4 pb-3 text-left">User</th>
                        <th className="px-4 pb-3 text-left">Email</th>
                        <th className="px-4 pb-3 text-right">Pets</th>
                        <th className="px-4 pb-3 text-right">Pictures</th>
                        <th className="px-4 pb-3 text-right">Notes</th>
                        <th className="px-4 pb-3 text-right">External Notes</th>
                        <th className="px-4 pb-3 text-right">Medications</th>
                        <th className="px-4 pb-3 text-right">Foods</th>
                        <th className="px-4 pb-3 text-right">Allergies</th>
                        <th className="px-4 pb-3 text-right">Vaccinations</th>
                        <th className="px-4 pb-3 text-right">Conditions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => {
                        const rowClass = index % 2 === 0
                          ? isDarkMode
                            ? 'bg-slate-900/40'
                            : 'bg-slate-50'
                          : '';
                        return (
                          <tr key={row.id} className={rowClass}>
                            <td className={tableCellClass}>
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {row.displayName}
                                  {row.superAdmin ? ' (super admin)' : ''}
                                </span>
                                <span className={`${mutedTextClass} text-xs`}>{row.id}</span>
                                {row.createdAt ? (
                                  <span className={`${mutedTextClass} text-xs`}>
                                    Joined {new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(new Date(row.createdAt))}
                                  </span>
                                ) : null}
                              </div>
                            </td>
                            <td className={`${tableCellClass} ${mutedTextClass}`}>{row.email || '—'}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petCount)}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petPictures)}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petNotes)}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petExternalNotes)}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petMedications)}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petFoods)}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petAllergies)}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petVaccinations)}</td>
                            <td className={`${tableCellClass} text-right`}>{formatCount(row.petConditions)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : null}
            </section>
          </>
        ) : null}

        <footer className={`${mutedTextClass} text-sm`}>
          Signed in as <span className="font-medium text-blue-500">{displayName}</span>
          {isSuperAdmin ? ' (super admin)' : null}.
        </footer>
      </div>
    </DashboardLayout>
  );
}
